import {
  type CSSProperties,
  type FocusEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useInRouterContext } from 'react-router';

import { helpTopicHref, type HelpTopicId, isHelpTopicId } from '@/shared/lib/help-topics';

import { useHelpTipSource } from './help-source';
import { Icons } from './icons';
import styles from './HelpTip.module.css';

interface Props {
  /** What the icon explains, for the accessible name: "About: Dates". */
  topic: string;
  /**
   * The Help Center article this tip belongs to. It adds a "Learn more" link and, when `text` is
   * not given, supplies the short explanation from the article itself.
   */
  topicId?: HelpTopicId;
  /** A local explanation (short, plain language). Needed only when no article text fits. */
  text?: string;
}

/**
 * A small "?" that explains one non-obvious control. Hover or keyboard focus shows the text as a
 * tooltip; a click / tap PINS it, and a pinned tip that belongs to an article also offers a
 * "Learn more" link, so it becomes a small non-modal dialog (a link cannot live in a tooltip).
 * A press elsewhere, Tab out of it or Escape dismisses it (Escape from inside returns the focus to
 * the "?"). No backdrop, no focus trap. The text is the button's `aria-describedby`, so screen
 * readers read it too, and the button never submits a surrounding form.
 */
export function HelpTip({ text, topic, topicId }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const source = useHelpTipSource();
  const inRouter = useInRouterContext();
  const button = useRef<HTMLButtonElement>(null);
  const root = useRef<HTMLSpanElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});
  const visible = hover || focus || pinned;
  const article = topicId !== undefined && isHelpTopicId(topicId) ? topicId : null;
  const shown = text ?? (article ? source?.tip(article) : undefined);
  const interactive = pinned && article !== null;
  const request = source?.request;

  // The guide text is fetched the first time a tip that needs it is actually shown.
  useEffect(() => {
    if (visible && text === undefined) request?.();
  }, [visible, text, request]);

  const place = useCallback(() => {
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const width = Math.min(260, window.innerWidth - 2 * margin);
    const left = Math.max(
      margin,
      Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - margin),
    );
    const below = window.innerHeight - rect.bottom;
    setStyle({
      left,
      width,
      ...(below < (article ? 180 : 140) && rect.top > below
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  }, [article]);

  useEffect(() => {
    if (!visible) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [visible, place, shown]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A press of Escape inside the popup hands the focus back to the "?" it came from.
      if (
        root.current?.contains(document.activeElement) &&
        document.activeElement !== button.current
      ) {
        button.current?.focus();
      }
      setPinned(false);
      setHover(false);
      setFocus(false);
    };
    const onPress = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setPinned(false);
      setHover(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [visible]);

  // Tabbing out of the tip (and its link) closes a pinned tip.
  const onBlurRoot = (event: FocusEvent<HTMLSpanElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && root.current?.contains(next)) return;
    if (next !== null) setPinned(false);
  };

  const close = () => {
    setPinned(false);
    setHover(false);
    setFocus(false);
  };

  const link =
    article &&
    (inRouter ? (
      <Link to={helpTopicHref(article)} className={styles.more} onClick={close}>
        {t('help.learnMore')}
        <Icons.arrowRight />
      </Link>
    ) : (
      <a href={helpTopicHref(article)} className={styles.more} onClick={close}>
        {t('help.learnMore')}
        <Icons.arrowRight />
      </a>
    ));

  return (
    <span
      ref={root}
      className={styles.root}
      onBlur={onBlurRoot}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setHover(true);
      }}
      onPointerLeave={() => {
        setHover(false);
      }}
    >
      <button
        ref={button}
        type="button"
        className={styles.button}
        aria-label={t('help.about', { topic })}
        aria-describedby={id}
        aria-expanded={visible}
        {...(article && { 'aria-haspopup': 'dialog' as const })}
        onFocus={(event) => {
          // Only keyboard focus opens it; a tap focuses too, and its click pins/unpins instead.
          if (event.currentTarget.matches(':focus-visible')) setFocus(true);
        }}
        onBlur={() => {
          setFocus(false);
        }}
        onClick={() => {
          setPinned((value) => !value);
        }}
      >
        <Icons.help />
      </button>
      <span
        id={id}
        role={interactive ? 'dialog' : 'tooltip'}
        {...(interactive && { 'aria-label': t('help.about', { topic }) })}
        className={styles.tip}
        style={style}
        hidden={!visible}
      >
        <span className={styles.text}>{shown ?? t('common.loading')}</span>
        {interactive && link}
      </span>
    </span>
  );
}
